/**
 * Display formatters for money and dates. Centralized so currency/locale
 * choices live in one place (the legacy app scattered `CurrencyFormat` +
 * `SimpleDateFormat`). All are defensive about null/NaN input.
 */
const DEFAULT_CURRENCY = "USD";

/** Format a number as currency. Falls back to a plain `$0.00` on bad input. */
export function formatCurrency(
  amount: number | null | undefined,
  currency: string = DEFAULT_CURRENCY,
): string {
  const value =
    typeof amount === "number" && Number.isFinite(amount) ? amount : 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

/** "2.5 MB" — human-readable byte size. */
export function formatBytes(bytes: number | null | undefined): string {
  const n = typeof bytes === "number" && Number.isFinite(bytes) ? bytes : 0;
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`;
}

function toDate(input: string | number | Date | null | undefined): Date | null {
  if (input === null || input === undefined || input === "") return null;
  const date = input instanceof Date ? input : new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Nov 2, 2024" — or an em dash when the date is missing/invalid. */
export function formatDate(
  input: string | number | Date | null | undefined,
): string {
  const date = toDate(input);
  if (!date) return "—";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

/** "Nov 2, 2024, 10:15 AM" — or an em dash when missing/invalid. */
export function formatDateTime(
  input: string | number | Date | null | undefined,
): string {
  const date = toDate(input);
  if (!date) return "—";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * "June 25, 2026 03:34 AM" — the account-dashboard stamp.
 *
 * Deliberately not `formatDateTime`: legacy's tables print the full month name
 * and a zero-padded 12-hour clock (`SimpleDateFormat(added, false)` followed by
 * a `toLocaleTimeString` in a `<sup>`), which reads differently from the
 * compact "Nov 2, 2024, 10:15 AM" used everywhere else. Built from two
 * formatters rather than one so the parts join on a plain space — a single
 * `Intl.DateTimeFormat` with both date and time fields emits "… , 2026 at …".
 */
export function formatDateTimeLong(
  input: string | number | Date | null | undefined,
): string {
  const date = toDate(input);
  if (!date) return "—";
  const day = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
  return `${day} ${time}`;
}

/**
 * "3 days ago" relative time (mirrors the legacy `TimeAgo`, minus its
 * `last_activity`-specific "online" quirk). Empty string when missing/invalid.
 */
export function formatRelativeTime(
  input: string | number | Date | null | undefined,
): string {
  const date = toDate(input);
  if (!date) return "";
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  // [unit, seconds-per-unit, upper-bound-in-seconds]
  const divisions: [Intl.RelativeTimeFormatUnit, number, number][] = [
    ["second", 1, 60],
    ["minute", 60, 3600],
    ["hour", 3600, 86400],
    ["day", 86400, 604800],
    ["week", 604800, 2629800],
    ["month", 2629800, 31557600],
    ["year", 31557600, Infinity],
  ];
  for (const [unit, perUnit, bound] of divisions) {
    if (abs < bound) return rtf.format(Math.round(seconds / perUnit), unit);
  }
  return rtf.format(Math.round(seconds / 31557600), "year");
}

/**
 * Human-readable duration from a number of minutes ("45 minutes", "2 hours",
 * "1 day 3 hours") — mirrors the legacy `formatDuration`.
 */
export function formatDuration(minutes: number | string | null | undefined): string {
  const min =
    typeof minutes === "string" ? parseInt(minutes, 10) : (minutes ?? 0);
  if (!Number.isFinite(min) || min <= 0) return "—";
  const plural = (n: number, unit: string) => `${n} ${unit}${n !== 1 ? "s" : ""}`;
  if (min < 60) return plural(min, "minute");
  if (min < 1440) {
    const hours = Math.floor(min / 60);
    const rem = min % 60;
    return rem > 0
      ? `${plural(hours, "hour")} ${plural(rem, "minute")}`
      : plural(hours, "hour");
  }
  const days = Math.floor(min / 1440);
  const remHours = Math.floor((min % 1440) / 60);
  return remHours > 0
    ? `${plural(days, "day")} ${plural(remHours, "hour")}`
    : plural(days, "day");
}
